import * as React from "react";
import Badge from "@mui/joy/Badge";
import Avatar, { AvatarProps } from "@mui/joy/Avatar";

/**
 * Props interface for the AvatarWithStatus component.
 * Extends AvatarProps with an online status indicator.
 * 
 * @interface AvatarWithStatusProps
 * @extends {AvatarProps}
 * @property {boolean} [online] - Whether the user is online
 */
type AvatarWithStatusProps = AvatarProps & {
  online?: boolean;
};

/**
 * A component that displays an avatar with an online status indicator.
 * Shows a green badge for online users and a neutral badge for offline users.
 * 
 * @component
 * @param {AvatarWithStatusProps} props - The props for the AvatarWithStatus component
 * @returns {JSX.Element} An avatar with a status badge
 * 
 * @example
 * ```tsx
 * <AvatarWithStatus
 *   online={true}
 *   src="/path/to/avatar.jpg"
 *   alt="User Avatar"
 * />
 * ```
 */
export default function AvatarWithStatus({
  online = false,
  ...rest
}: AvatarWithStatusProps) {
  return (
    <div>
      <Badge
        color={online ? "success" : "neutral"}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeInset="6px 6px"
      >
        <Avatar {...rest} />
      </Badge>
    </div>
  );
}
